import { Link } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/AdminLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Building2, Users, Calendar, CreditCard } from 'lucide-react'

interface Props {
  stats: {
    total_organizations: number
    total_users: number
    bookings_this_week: number
    bookings_this_month: number
    subscriptions: {
      active: number
      trialing: number
      canceled: number
      past_due: number
    }
  }
  recentOrgs: Array<{
    id: number
    name: string
    slug: string
    owner_name: string | null
    subscription_status: string
    subscription_plan: string
    created_at: string
  }>
  onboardingFunnel: Array<{ step: string; count: number }>
  totalOrgs: number
}

const STEP_LABELS: Record<string, string> = {
  services: 'Servicios',
  demo_conversation: 'Prueba del agente',
  whatsapp: 'WhatsApp',
}

function planBadge(plan: string, status: string) {
  const planMap: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' | 'default' }> = {
    pro: { label: 'Pro', variant: 'default' },
    basic: { label: 'Basic', variant: 'secondary' },
    free: { label: 'Free', variant: 'secondary' },
  }
  const statusMap: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
    active: { label: 'Activo', variant: 'success' },
    trialing: { label: 'Trial', variant: 'warning' },
    canceled: { label: 'Cancelado', variant: 'destructive' },
    past_due: { label: 'Vencido', variant: 'destructive' },
  }
  const planInfo = planMap[plan] || { label: plan, variant: 'secondary' as const }
  const statusInfo = statusMap[status]
  return (
    <div className="flex items-center gap-1.5">
      <Badge variant={planInfo.variant}>{planInfo.label}</Badge>
      {statusInfo && status !== 'active' && (
        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
      )}
    </div>
  )
}

export default function Dashboard({ stats, recentOrgs, onboardingFunnel, totalOrgs }: Props) {
  return (
    <AdminLayout>
      <div className="space-y-8">
        <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>

        {/* Stat cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={Building2} label="Organizaciones" value={stats.total_organizations} />
          <StatCard icon={Users} label="Usuarios" value={stats.total_users} />
          <StatCard icon={Calendar} label="Citas esta semana" value={stats.bookings_this_week} />
          <StatCard icon={Calendar} label="Citas este mes" value={stats.bookings_this_month} />
        </div>

        {/* Subscriptions breakdown */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={CreditCard} label="Activos" value={stats.subscriptions.active} />
          <StatCard icon={CreditCard} label="Trial" value={stats.subscriptions.trialing} />
          <StatCard icon={CreditCard} label="Cancelados" value={stats.subscriptions.canceled} />
          <StatCard icon={CreditCard} label="Vencidos" value={stats.subscriptions.past_due} />
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          {/* Recent signups */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Registros recientes</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organización</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentOrgs.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell>
                        <Link href={`/admin/organizations/${org.id}`} className="font-medium hover:underline">
                          {org.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{org.owner_name || '—'}</TableCell>
                      <TableCell>{planBadge(org.subscription_plan, org.subscription_status)}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {new Date(org.created_at).toLocaleDateString('es-MX')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Onboarding funnel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Embudo de onboarding</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {onboardingFunnel.map((item) => {
                  const pct = totalOrgs > 0 ? Math.round((item.count / totalOrgs) * 100) : 0
                  return (
                    <div key={item.step} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground">{STEP_LABELS[item.step] || item.step}</span>
                        <span className="text-muted-foreground">
                          {item.count}/{totalOrgs} ({pct}%)
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: number
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  )
}
